import {
  createExperimentationAccountContext,
  createExperimentationAppContext,
  createExperimentationContext,
  createExperimentationOrganizationContext,
  createExperimentationUserContext,
} from '@syconium/weeping-figs'

import { LocalizationAttributes } from './Localization.config'
import { SessionAttributes } from './Session.config'

import type { AccountAuthorization } from './Authentication.config'
import type { LDOptions as LDServerOptions } from '@launchdarkly/node-server-sdk'

type NecessarySessionAttributes = SessionAttributes & {
  visitorId: NonNullable<SessionAttributes['visitorId']>
}

type NecessaryLocalizationAttributes = LocalizationAttributes & {
  languageGroup: NonNullable<LocalizationAttributes['languageGroup']>
  region: NonNullable<LocalizationAttributes['region']>
}

export type ExperimentationContext = ReturnType<typeof createExperimentationContext>

export const createLaunchDarklyContext = ({
  account: authorizedAccount,
  sessionAttributes,
  localizationAttributes,
  portal,
  activeTeamStore,
}: {
  account: AccountAuthorization | undefined
  sessionAttributes: NecessarySessionAttributes
  localizationAttributes: NecessaryLocalizationAttributes
  portal?: {
    id: string
    name: string
  }
  activeTeamStore?: {
    id: string
    name: string
    organization: {
      id: string
      name: string
    }
  }
}) => {
  const app = createExperimentationAppContext({
    name: 'magnolia',
    version: process.env.NEXT_PUBLIC_MAGNOLIA_VERSION ?? '0.0.0',
  })

  const user = createExperimentationUserContext({
    visitorId: sessionAttributes.visitorId,
    email: authorizedAccount?.email,
    stylePreference: sessionAttributes.stylePreference,
    customerType: sessionAttributes.customerType,
    languagePreference: localizationAttributes.languageGroup,
    shoppingRegion: localizationAttributes.region.id,
  })

  const account = createExperimentationAccountContext({
    userId: authorizedAccount?.id,
    visitorId: sessionAttributes.visitorId,
  })

  const organization = (() => {
    // If the user is associated with an active team store, identify their organization context
    // based on the active team store & it's associated organization
    if (activeTeamStore) {
      return createExperimentationOrganizationContext<{
        teamStoreId: string
        teamStoreName: string
      }>({
        id: activeTeamStore.organization.id,
        name: activeTeamStore.organization.name,
        customProperties: {
          teamStoreId: activeTeamStore.id,
          teamStoreName: activeTeamStore.name,
        },
      })
    }

    // Otherwise, if the user is associated with a portal, identify their organization context based on their associated portal
    if (portal) {
      return createExperimentationOrganizationContext({ id: portal.id, name: portal.name })
    }

    // Don't define an organization context for users who aren't associated with either an active team store or a portal
    return undefined
  })()

  return createExperimentationContext({
    app,
    user,
    account,
    organization,
  })
}

export const createLaunchDarklyServerConfig = (): LDServerOptions => {
  return {
    stream: process.env.NEXT_PUBLIC_FIGS_ENV !== 'local',
    pollInterval: 900, // Ignored if stream is true. Otherwise 15 minute flag update checks.
    streamInitialReconnectDelay: process.env.NEXT_PUBLIC_FIGS_ENV !== 'local' ? 3 : 900,
    sendEvents: true,
    diagnosticRecordingInterval: 3600,
    application: {
      name: 'magnolia',
      version: process.env.NEXT_PUBLIC_MAGNOLIA_VERSION ?? '0.0.0',
    },
  }
}
